<template>
  <div class="details">
    <div class="details__title px-5"></div>

    <div class="details__content container">
      <h1 class="title is-3">{{ $t("cars.title") }}</h1>
      <div class="columns is-multiline details__content__box m-0 mb-5">
        <div
          class="field column is-one-third-desktop"
          v-if="
            companyInfo.userSettings?.canBookOnBehalfOfOthers &&
            companyInfo.allowListedEmployees &&
            businessUnits
          "
        >
          <h5 class="title is-5">
            {{ $t("CarReservationForm.findDriver") }}
          </h5>
          <p style="margin-bottom: 10px">
            {{ $t("CarReservationForm.findDriverComment") }}
          </p>
          <EmployeeSelection
            :business-units="businessUnits"
            @selected-employee="addEmployeeDriver"
          />
          <p v-if="companyInfo.allowListedEmployees" class="mt-3">
            {{ $t("CarReservationForm.unknownEmployee") }}
          </p>
        </div>

        <div
          class="field column is-one-third-desktop"
          v-if="
            !companyInfo?.userSettings?.canBookOnBehalfOfOthers ||
            (!companyInfo.allowUnlistedEmployees &&
              !companyInfo.allowListedEmployees)
          "
        >
          <h5 class="title is-5">
            {{ $t("CarReservationForm.personalReservationHeader") }}
          </h5>
          <p>{{ $t("CarReservationForm.personalReservation") }}</p>
        </div>

        <div
          class="field column is-one-third-desktop"
          v-if="
            companyInfo.userSettings?.canBookOnBehalfOfOthers &&
            companyInfo.allowUnlistedEmployees
          "
        >
          <AddUnlistedEmployee
            :is-cars="true"
            @created-unlisted-employee="addUnlistedEmployeeDriver"
          />
        </div>

        <div class="field column is-one-third-desktop">
          <template v-if="Driver.lastName">
            <h5 class="title is-5">
              {{ $t("CarReservationForm.selectedDriver") }}
            </h5>
            <div
              class="is-flex is-align-items-center is-justify-content-space-between mb-2"
            >
              <span class="u-weight--bold"
                >{{ Driver.firstName }} {{ Driver.lastName }}</span
              >
              <button
                class="c-button c-button--small is-danger"
                @click.stop.prevent="removeSelectedDriver"
              >
                <span class="icon is-small">
                  <i class="fas fa-times"></i>
                </span>
              </button>
            </div>
            <br />
          </template>
          <SelectedBusinessUnit
            :business-units="businessUnits"
            :selected-business-unit="selectedBusinessUnit"
            required
            @selected-business-unit="selectBusinessUnit"
            @remove-business-unit="removeBusinessUnit"
          />
        </div>
      </div>
      <div class="details__content__box mb-5">
        <h5 class="title is-5">
          {{ $t("CarReservationForm.selectCar") }}
        </h5>
        <div class="columns">
          <div class="column is-half">
            <div class="field">
              <label class="label u-text-color-brand">{{
                $t("CarReservationForm.SelectCarClass")
              }}</label>
              <div class="control">
                <div class="select is-fullwidth">
                  <select v-model="selectedCarClass">
                    <option value="" disabled selected>
                      {{ $t("CarReservationForm.SelectCarClassPlaceholder") }}
                    </option>
                    <option
                      v-for="category in carCategories"
                      :key="category.value"
                      :value="category.value"
                    >
                      {{ category.label }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div class="column is-half">
            <div class="field">
              <label class="label u-text-color-brand">{{
                $t("CarReservationForm.SelectCarTransmission")
              }}</label>
              <div class="control">
                <div class="select is-fullwidth">
                  <select v-model="selectedCarTransmissionType">
                    <option value="" disabled selected>
                      {{
                        $t(
                          "CarReservationForm.SelectCarTransmissionPlaceholder"
                        )
                      }}
                    </option>
                    <option
                      v-for="transmission in carTransmissionTypes"
                      :key="transmission.value"
                      :value="transmission.value"
                    >
                      {{ transmission.label }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="columns">
          <div class="column is-half">
            <div class="field">
              <label class="label u-text-color-brand">{{
                $t("CarReservationForm.PickupLocation")
              }}</label>
              <div class="control">
                <input
                  type="text"
                  v-model="pickupLocation"
                  id="pickupLocation"
                  name="pickupLocation"
                  class="input"
                  :placeholder="
                    $t('CarReservationForm.PickupLocationPlaceholder')
                  "
                  autocomplete="off"
                />
              </div>
            </div>
            <Datepicker
              v-model="pickupDateCar"
              :enableTimePicker="true"
              :format="formatDateTime"
              :minDate="new Date()"
              locale="nl"
            />
          </div>
          <div class="column is-half">
            <div class="field">
              <label class="label u-text-color-brand">{{
                $t("CarReservationForm.ReturnLocation")
              }}</label>
              <div class="control">
                <input
                  type="text"
                  v-model="returnLocation"
                  id="returnLocation"
                  name="returnLocation"
                  class="input"
                  :placeholder="
                    $t('CarReservationForm.ReturnLocationPlaceholder')
                  "
                  autocomplete="off"
                />
              </div>
            </div>
            <Datepicker
              v-model="returnDateCar"
              :enableTimePicker="true"
              :format="formatDateTime"
              locale="nl"
              :minDate="pickupDateCar ?? new Date()"
            />
          </div>
        </div>
      </div>

      <form
        @submit.prevent="makeReservation()"
        class="details__content__box columns is-multiline details__content__box m-0 mb-5"
      >
        <div class="field column is-full">
          <label for="notes">{{ $t("CarReservationForm.notes") }}</label>
          <div class="control">
            <textarea
              class="textarea"
              v-model="notes"
              id="notes"
              name="notes"
              rows="5"
              :placeholder="$t('CarReservationForm.notesPlaceholder')"
            ></textarea>
          </div>
          <CustomField
            v-for="field in companyInfo.customFields"
            :key="field.name"
            :customField="field"
            @setted-value="setValue"
          ></CustomField>
        </div>
        <div
          class="field is-fullwidth column is-flex is-justify-content-flex-end"
        >
          <button
            class="c-button c-button--primary mt-4"
            :disabled="disableButton"
            type="submit"
          >
            {{ $t("CarReservationForm.sendRequest") }}
          </button>
        </div>
      </form>
    </div>
    <NotificationMessage
      :level="notification.level"
      :autohide="false"
      :title="notification.title"
      :message="notification.message"
      :show="notification.show"
      @toggle="toggleNotification"
    />
  </div>
</template>

<script>
import {
  getCarCategories,
  getCarTransmissionTypes,
  formatDateTime,
  createDriverFromEmployee,
  createUnlistedDriver,
  createPersonalDriver,
  validateCarDates,
  validateCarLocations,
  validateBusinessUnit,
  validateDriver,
  validateCustomFields,
  prepareCarReservationData,
  prepareCarTrackingData
} from "@/helpers/car.helper.js";
import CommonHelper from "@/helpers/common.helper.js";
import moment from "moment";
import AddUnlistedEmployee from "./components/reservationform/AddUnlistedEmployee.vue";
import SelectedBusinessUnit from "./components/reservationform/SelectedBusinessUnit.vue";
import CustomField from "./components/reservationform/CustomField.vue";
import EmployeeSelection from "./components/reservationform/EmployeeSelection.vue";

export default {
  components: {
    SelectedBusinessUnit,
    EmployeeSelection,
    AddUnlistedEmployee,
    CustomField
  },
  data() {
    return {
      changesAllowed: true,
      isLoading: false,
      isPricesLoading: false,
      disableButton: false,
      notes: "",
      showEditableFields: false,
      returnedData: null,
      businessUnits: null,
      allowBusinessUnitClearing: false,
      selectedBusinessUnit: null,
      selectedCarClass: "",
      selectedCarTransmissionType: "",
      pickupLocation: "",
      returnLocation: "",
      errorMessages: [],
      companyInfo: {},
      reservationMeta: {
        businessUnit: null,
        location: null,
        dateRange: {
          startDate: null,
          endDate: null,
          startTime: null,
          endTime: null
        }
      },
      Driver: {},
      pickupDateCar: null,
      returnDateCar: null,
      customFieldFormData: {},
      isSaving: false,
      notification: {
        show: false,
        title: "",
        message: "",
        level: "info"
      }
    };
  },
  computed: {
    carCategories() {
      return getCarCategories(this.$t);
    },
    carTransmissionTypes() {
      return getCarTransmissionTypes(this.$t);
    },
    formatDateTime() {
      return formatDateTime;
    }
  },
  async created() {
    this.getCompanyInfo();

    let companyInfo = await CommonHelper.getCompanyInfo();
    if (companyInfo && companyInfo.budget) {
      this.budget = companyInfo.budget;
    }
  },
  methods: {
    loadDetails() {},
    setValue(data) {
      this.customFieldFormData[data.field] = data.value;
    },
    async getCompanyInfo() {
      this.companyInfo = await CommonHelper.getCompanyInfo();

      this.companyInfo.customFields.sort((a, b) =>
        Number(a.mandatory) > Number(b.mandatory) ? -1 : 1
      );

      this.businessUnits = await CommonHelper.getBusinessUnits();
    },
    selectBusinessUnit(data) {
      this.selectedBusinessUnit = data.businessUnit;
    },
    removeBusinessUnit() {
      this.selectedBusinessUnit = null;
    },
    addEmployeeDriver(data) {
      if (data?.employee) {
        this.Driver = createDriverFromEmployee(data.employee);
        if (this.selectedBusinessUnit == null && data.businessUnit) {
          this.selectBusinessUnit(data);
        }
      } else {
        this.driverEmployee = null;
      }
    },
    addUnlistedEmployeeDriver(data) {
      if (data?.employee) {
        this.Driver = createUnlistedDriver(data.employee);
      } else {
        this.driverEmployee = null;
      }
    },
    removeSelectedDriver() {
      this.Driver = {};
      this.selectedBusinessUnit = null;
    },
    async makeReservation() {
      // set driver if personal reservation
      if (!this.companyInfo?.userSettings?.canBookOnBehalfOfOthers) {
        this.Driver = createPersonalDriver(this.companyInfo);
      }

      let url = `${this.AVAILABILITY_API_URL}/reservation/car`;
      let dataIsValid = this.validateForm();

      if (dataIsValid) {
        this.isSaving = true;

        // Extract date and time from datetime objects
        const carPickupTime = this.pickupDateCar
          ? moment(this.pickupDateCar).format("HH:mm:ss")
          : null;
        const carReturnTime = this.returnDateCar
          ? moment(this.returnDateCar).format("HH:mm:ss")
          : null;

        const formData = {
          carClass: this.selectedCarClass,
          transmissionType: this.selectedCarTransmissionType,
          departureLocation: this.pickupLocation,
          destinationLocation: this.returnLocation,
          departureDate: this.pickupDateCar
            ? moment(this.pickupDateCar).format("YYYY-MM-DD")
            : null,
          departureTime: carPickupTime,
          returnDate: this.returnDateCar
            ? moment(this.returnDateCar).format("YYYY-MM-DD")
            : null,
          returnTime: carReturnTime,
          customFields: this.customFieldFormData,
          notes: this.notes,
          businessUnitId: this.selectedBusinessUnit?.id || null,
          vehicleType: "RENTAL_CAR",
          driver: this.Driver
        };

        let saveData = { reservationList: [formData] };
        this.saveData = JSON.stringify(saveData, null, " ");
        this.disableButton = true;

        const { status, data } = await this.axios.post(url, saveData);

        if (status == 200) {
          this.notification.title = this.$t("CarReservationForm.savedTitle");
          this.notification.message = this.$t("success");
          this.notification.show = true;
          this.notification.level = "success";

          setTimeout(() => {
            this.notification.show = false;
            this.disableButton = false;
          }, 30 * 1000);

          let trackData = prepareCarTrackingData(formData, "success");
          this.track("carReservation", trackData);

          this.$router.push("/car-confirmation");
        } else {
          this.notification.title = this.$t("CarReservationForm.errorTitle");
          this.notification.message = this.$t(
            "CarReservationForm.errorMessage"
          );
          this.notification.show = true;
          this.notification.level = "danger";
          this.disableButton = false;

          setTimeout(() => {
            this.notification.show = false;
          }, 30 * 1000);
        }
        this.isSaving = false;
      }
    },
    validateForm() {
      this.errorMessages = [];
      this.showNotification = false;

      let allErrors = [];

      // Validate business unit
      allErrors.push(
        ...validateBusinessUnit(
          this.companyInfo,
          this.selectedBusinessUnit,
          this.$t
        )
      );

      // Validate driver
      allErrors.push(...validateDriver(this.Driver, this.$t));

      // Validate dates
      allErrors.push(
        ...validateCarDates(this.pickupDateCar, this.returnDateCar, this.$t)
      );

      // Validate locations
      allErrors.push(
        ...validateCarLocations(
          this.pickupLocation,
          this.returnLocation,
          this.$t
        )
      );

      // Validate custom fields
      allErrors.push(
        ...validateCustomFields(
          this.companyInfo.customFields,
          this.customFieldFormData,
          this.getFieldNameTranslation,
          this.$t
        )
      );

      this.errorMessages = allErrors;

      if (this.errorMessages.length > 0) {
        let notificationMessage = `${this.$t("error.genericIntro")} <ul>
      <li>- ${this.errorMessages.join("</li><li>- ")}</li></ul>`;

        this.notification.title = this.$t("error.genericTitle");
        this.notification.message = notificationMessage;
        this.notification.level = "danger";
        this.notification.show = true;

        setTimeout(() => {
          this.notification.show = false;
        }, 10 * 1000);
        return false;
      } else {
        return true;
      }
    },
    getTitle(field) {
      return this.getFieldNameTranslation(field);
    },
    toggleNotification() {
      this.notification.show = !this.notification.show;
    },
    getFieldNameTranslation(field) {
      // your custom method if needed
      return field.label || field.name;
    }
  }
};
</script>
<style scoped>
.details__title {
  background: url("@/assets/images/car-bg.jpg") center center no-repeat;
  background-size: cover;
}

.dropdown-item {
  cursor: pointer;
  padding: 5px;
}

.dropdown-item:hover {
  background-color: #f1f1f1;
}
</style>
